import React, {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

enum Role {
  SUPER_ADMIN,
  ADMIN,
  USER,
}

export type User = {
  id?: string;
  email?: string;
  username?: string;
  firstName?: string;
  lastName?: string;
  isAdmin?: boolean;
  role?: Role;
  password?: string;
  isLoggedIn?: boolean;
  created_at?: string;
  updated_at?: string;
};

type UserContextType = {
  user: User | null;
  setUser?: Dispatch<SetStateAction<User | null>>;
  error?: ErrorProps;
};

type ErrorProps = {
  message?: string;
};

export const UserContext = createContext<UserContextType>({ user: null });

export const useUser = () => {
  const user = useContext(UserContext);

  if (!user) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return user;
};

export const UserProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [error] = useState<ErrorProps>();

  const ctx = useMemo(() => ({ user, setUser, error }), [error, user]);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);

  useEffect(() => {
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    } else {
      localStorage.removeItem('user');
    }
  }, [user]);

  return <UserContext.Provider value={ctx}>{children}</UserContext.Provider>;
};
